import { isPrimaryRoleId, type PrimaryRoleId, RoleId } from '@common/constants';
import type { User, UserRole } from '@common/types/users';

export function parseUserDetails(userDetails) {
    return userDetails.map((userDetail) => {
        try {
            return JSON.parse(userDetail);
        } catch (error) {
            console.error('Failed to parse user detail:', error);
            return null;
        }
    });
}

export function hasOrganizationalAdminRole(roles: UserRole[] | undefined): boolean {
    return !!roles?.some((role) => role.roleId === RoleId.ORGANIZATIONAL_ADMIN);
}

interface UserWithRoles extends Pick<User, 'lawFirmId'> {
    roles?: UserRole[];
}

/**
 * Whether the user may add, edit, disable and delete the firm's users: a Super Admin, or one of the
 * firm's own Organizational Admins. The backend allows the same, by permission and firm
 */
export function canManageFirmUsers(
    user: UserWithRoles | null | undefined,
    lawFirmId: number | null | undefined,
): boolean {
    if (!user) return false;
    if (user.roles?.some((role) => role.roleId === RoleId.SUPER_ADMIN)) return true;
    return (
        lawFirmId !== null &&
        lawFirmId !== undefined &&
        user.lawFirmId === lawFirmId &&
        hasOrganizationalAdminRole(user.roles)
    );
}

export function getPrimaryRoleId(roles: UserRole[]): PrimaryRoleId | undefined {
    for (const role of roles) {
        if (isPrimaryRoleId(role.roleId)) {
            return role.roleId;
        }
    }
    return undefined;
}

export function getRolesFromRolesArray(roles: UserRole[]): string | undefined {
    if (roles.length === 1) {
        return roles[0].name ?? undefined;
    }

    if (roles.length >= 1) {
        const requiredRoleObj = roles.find((role) => isPrimaryRoleId(role.roleId));
        if (requiredRoleObj) {
            return requiredRoleObj.name ?? undefined;
        }
    }

    return undefined;
}

interface ContactDetail {
    id: number;
    name: string;
    email: string;
    phone: string;
    category: string;
}

interface FormResponseEntry {
    Name: string;
    Title: string;
    Value: string;
    TemplateFormFieldId: string;
    DisplayOrder: number;
    FormGroupTitle: string | null;
}

export const extractContactDetails = (formResponse: FormResponseEntry[]): ContactDetail[] => {
    const details: ContactDetail[] = [];

    if (Array.isArray(formResponse)) {
        const firstName = formResponse.find((i) => i.Name === 'firstName')?.Value || '';
        const lastName = formResponse.find((i) => i.Name === 'lastName')?.Value || '';
        const middleName = formResponse.find((i) => i.Name === 'middleName')?.Value || '';
        const email = formResponse.find((i) => i.Name === 'email')?.Value || '';
        const phoneRaw = formResponse.find((i) => i.Name === 'primaryPhone')?.Value || '';
        const phone = phoneRaw.replace(/\D/g, '');

        if (firstName && lastName) {
            details.push({
                id: Date.now() + Math.floor(Math.random() * 1000),
                name: `${firstName} ${middleName} ${lastName}`.trim(),
                email,
                phone,
                category: 'Primary Contact',
            });
        }
    }

    if (!formResponse || typeof formResponse !== 'object') return details;

    formResponse
        .filter((item) => item.Name === 'userContactDetails')
        .forEach((entry) => {
            try {
                const parsedUserContactDetails = JSON.parse(entry.Value ?? '[]');

                if (!Array.isArray(parsedUserContactDetails)) return;

                parsedUserContactDetails.forEach((contactDetailString: string) => {
                    try {
                        const finalUserContactDetails: FormResponseEntry[] =
                            JSON.parse(contactDetailString);

                        if (!Array.isArray(finalUserContactDetails)) return;

                        const extracted = Object.fromEntries(
                            finalUserContactDetails
                                .filter((item) => item.Name && item.Value !== undefined)
                                .map((item) => [item.Name, item.Value]),
                        );

                        const phone = (extracted.primaryPhone || '').replace(/\D/g, '');
                        const name = [extracted.firstName, extracted.middleName, extracted.lastName]
                            .filter(Boolean)
                            .join(' ');

                        details.push({
                            id: Date.now() + Math.floor(Math.random() * 1000),
                            name,
                            email: extracted.email || '',
                            phone,
                            category: 'Other',
                        });
                    } catch (error) {
                        console.error('Error parsing individual contact detail:', error);
                    }
                });
            } catch (error) {
                console.error('Error parsing userContactDetails Value:', error);
            }
        });

    return details;
};
